import { mortgageSummary } from "../HelperFunctions/mortgageSummary";
// ./Report/dccReport.jsx
import { Document, Page, Text, View, Image } from "@react-pdf/renderer";
import LogoIcon from "../../../../css/WhiteLabelledDocuments/logo";
import DCCReportStyles from "./styles/dccReportStyles";
import dayjs from "dayjs";
import Header from "./header";
import Footer from "./footer";
import {
  DebtConsolidationProcessIcon,
  FactFindIcon,
  IllustrationIcon,
  ClientUnderstandingIcon,
  AdditionalInformationIcon,
} from "../Report/assets/icons";

/* ---------- formatters (PDF-safe) ---------- */
const isNum = (n) => Number.isFinite(n);
const safeFixed = (n, d = 2) => (isNum(n) ? Number(n.toFixed(d)) : NaN);
const toMoney = (n) =>
  isNum(n) ? `£${safeFixed(n, 2).toLocaleString("en-GB")}` : "";
const toPct = (n) => (isNum(n) ? `${safeFixed(n, 2)}%` : "");
const dashIfNaN = (n) => (isNum(n) ? toMoney(n) : "");
const fmtInt = (n) => (isNum(n) ? String(n) : "");
const fmtText = (v, fallback = "") => {
  if (v === 0) return "";
  if (v === "" || v === null || v === undefined) return fallback;
  return String(v);
};
const plural = (n, s, p) => (n === 1 ? s : p);
const formatYearsMonths = (m) => {
  if (!isNum(m)) return "-";
  const years = Math.floor(m / 12);
  const months = m % 12;
  const parts = [];
  if (years) parts.push(`${years} ${plural(years, "year", "years")}`);
  if (months) parts.push(`${months} ${plural(months, "month", "months")}`);
  return parts.length ? parts.join(" ") : "-";
};

/* ---------- row helpers ---------- */
const HeaderRow = ({ headers, styles, flexes = [] }) => (
  <View style={styles.tableRow}>
    {headers.map((h, i) => (
      <Text key={i} style={[styles.tableHeader, { flex: flexes[i] ?? 1 }]}>
        {h}
      </Text>
    ))}
  </View>
);

const DataRow = ({
  cells,
  styles,
  flexes = [],
  alt = false,
  alignRights = [],
}) => (
  <View
    style={[
      styles.tableRow,
      alt ? styles.alternateRow : null,
      { alignItems: "center", justifyContent: "flex-start" },
    ]}
  >
    {cells.map((c, i) => {
      const cellStyle = {
        flex: flexes[i] ?? 1,
        textAlign: alignRights[i] ? "right" : "left",
        paddingLeft: !alignRights[i] ? 4 : 0,
        paddingTop: 2,
        paddingBottom: 2,
      };

      const isNode = typeof c === "object" && c !== null; // React element or object

      // If it's a React element (View/Text), render it inside a View cell wrapper
      if (isNode) {
        return (
          <View key={i} style={cellStyle}>
            {c}
          </View>
        );
      }

      // Otherwise render normal text
      return (
        <Text key={i} style={[styles.content, cellStyle]}>
          {String(c ?? "")}
        </Text>
      );
    })}
  </View>
);

/* ---------- reference APR table ---------- */
const averageInterestRates = [
  { type: "Credit Card", goodCredit: "~ 20% - 25%", higherRisk: "~ 30% - 40%" },
  {
    type: "Store Card (Retail Card)",
    goodCredit: "~ 25% - 30%",
    higherRisk: "~ 35% - 45%",
  },
  { type: "Overdraft", goodCredit: "~ 20% - 30%", higherRisk: "~ 30% - 40%" },
  {
    type: "Unsecured Loan (Personal)",
    goodCredit: "~ 5% - 10%",
    higherRisk: "~ 15% - 30%",
  },
  { type: "Secured Loan", goodCredit: "~ 4% - 8%", higherRisk: "~ 10% - 20%" },
  { type: "Car Finance", goodCredit: "~ 6% - 12%", higherRisk: "~ 12% - 25%" },
];

const DCCReport = ({
  primaryColor,
  logo,
  tradingName,
  regulatoryText,
  userEmail,
  customerName,
  adviserName,
  debts = [],
  currentMortgage = {},
  currentMortgages,
  loanAmount,
  initialApr,
  svrApr,
  initialYears,
  initialMonths,
  termYears,
  termMonths,
  feeMode,
  feeValue,
  totals = {},
  narrative,
  /* NEW: show if user typed overrides in the UI */
  initialMonthlyOverride,
  reversionMonthlyOverride,
}) => {
  const styles = DCCReportStyles(primaryColor || "081e56");

  const feeDisplay =
    feeMode === "percent"
      ? `${safeFixed(feeValue || 0, 2)}%`
      : `${toMoney(feeValue || 0)}`;

  const initialRate = `${toPct(initialApr || 0)}`;
  const svrRate = `${toPct(svrApr || 0)}`;

  const formatPeriod = (years, months) => {
    const yNum = Number(years);
    const mNum = Number(months);

    const y =
      isNum(yNum) && yNum > 0
        ? `${fmtInt(yNum)} year${yNum > 1 ? "s" : ""}`
        : "";

    const m =
      isNum(mNum) && mNum > 0
        ? `${fmtInt(mNum)} month${mNum > 1 ? "s" : ""}`
        : "";

    return [y, m].filter(Boolean).join(" ") || "0 months";
  };

  const initialPeriodText = formatPeriod(initialYears, initialMonths);
  const loanTermText = formatPeriod(termYears, termMonths);

  const {
    weightedApr,
    nExisting,
    months,
    fee,
    totalMonthly,
    monthlyPayInitial,
    monthlyPayReversion,
    monthlyPayNew,
    totalPaymentsExisting,
    totalInterestExisting,
    warnNeverPaysOff,
    totalInterestNew,
    totalPaymentsNew,
  } = totals || {};

  const isBlank = (v) => v == null || String(v).trim() === "";

  const mortgages = currentMortgages || [currentMortgage || {}];
  const summary = mortgageSummary(mortgages);
  /* Prefer the same display logic as UI for “Monthly payments preview” widgets */
  const initialMonthlyDisplay =
    initialMonthlyOverride !== "" && initialMonthlyOverride != null
      ? Number(initialMonthlyOverride)
      : isNum(monthlyPayInitial) && monthlyPayInitial > 0
        ? Number(monthlyPayInitial)
        : NaN;

  const reversionMonthlyDisplay =
    reversionMonthlyOverride !== "" && reversionMonthlyOverride != null
      ? Number(reversionMonthlyOverride)
      : isNum(monthlyPayReversion) && monthlyPayReversion > 0
        ? Number(monthlyPayReversion)
        : NaN;

  const initialWasOverridden =
    initialMonthlyOverride !== "" && initialMonthlyOverride != null;
  const reversionWasOverridden =
    reversionMonthlyOverride !== "" && reversionMonthlyOverride != null;

  return (
    <Document>
      {/* ========= Cover ========= */}
      <Page style={styles.pageWithChrome}>
        <View style={styles.graphicWrapper}>
          <View style={styles.graphicBox} />
          <View style={styles.graphicBox1} />
          <View style={styles.graphicBox2} />
          <View style={styles.graphicBox3} />
        </View>
        <View style={styles.graphicWrapper}>
          <View style={styles.graphicBox4} />
          <View style={styles.graphicBox5} />
          <View style={styles.graphicBox6} />
          <View style={styles.graphicBox7} />
          <View style={styles.graphicBox8} />
        </View>

        <View style={styles.centeredContent}>
          {logo ? <Image style={styles.image} src={logo} /> : <LogoIcon />}
          <Text style={styles.header}>{fmtText(tradingName, "Company")}</Text>
          <Text style={styles.subheading}>
            Debt Consolidation for: {fmtText(customerName, "Client Name")}
          </Text>
          {/* {fmtText(adviserName, "") ? (
            <Text style={styles.subheading}>Adviser: {adviserName}</Text>
          ) : null} */}
          {fmtText(userEmail, "") ? (
            <Text style={styles.subheading}>{userEmail}</Text>
          ) : null}
          <Text style={styles.subheading}>
            {dayjs().format("MMMM D, YYYY")}
          </Text>
        </View>
      </Page>

      {/* ========= Inputs: Existing Debts + Mortgage ========= */}
      <Page style={styles.pageWithChrome} wrap>
        <Header primaryColor={primaryColor} />
        <Footer primaryColor={primaryColor} regulatoryText={regulatoryText} />
        <Text style={styles.content}>
          This calculator provides illustrative examples only. It does not
          constitute financial advice. Individual circumstances vary, and
          outcomes may differ from the examples shown.
        </Text>
        <Text style={styles.content}>
          Consolidating unsecured debt into a mortgage may reduce monthly
          payments, but it increases the overall debt term and total interest
          paid. Your home is used as security, which means failure to meet
          repayments could result in repossession.
        </Text>
        {mortgages.length > 1 && (
          <View>
            <Text style={styles.subheading}>Combined mortgages</Text>
            <Text>Total balance: {toMoney(summary.balance)}</Text>
            <Text>Total monthly payment: {toMoney(summary.monthly)}</Text>
            <Text>Weighted current interest rate: {toPct(summary.apr)}</Text>
            <Text>Interest rates are weighted by outstanding balance. Each mortgage is projected separately.</Text>
          </View>
        )}
        {mortgages.map((cm, index) => {

  const cmBal = Number(cm.balance || 0);
  const cmApr = Number(cm.apr || 0);
  const cmMonthly = Number(cm.monthly || 0);
  const cmTermMonths =
    Number(cm.termYears || 0) * 12 + Number(cm.termMonths || 0);

  // stepped (optional)
  const cmInitApr = Number(cm.initialApr || 0);
  const cmSvrApr = Number(cm.svrApr || 0);
  const cmSvrMonthly = Number(cm.svrMonthly || 0);
  const cmInitPeriodMonths =
    Number(cm.initialYears || 0) * 12 + Number(cm.initialMonths || 0);
  const cmInitialMonths = Number(cm.initialMonths || 0);
  const cmInitialYears = Number(cm.initialYears || 0);


          return <View key={cm.id || index}>
        <Text style={styles.subheading}>{mortgages.length > 1 ? `Current Mortgage ${index + 1}` : "Current Mortgage"}</Text>
        <View style={styles.table}>
          <HeaderRow
            styles={styles}
            headers={["Entity", "Value"]}
            flexes={[1.5, 1]}
          />
          <DataRow
            styles={styles}
            cells={["Balance", toMoney(cmBal)]}
            flexes={[1.5, 1]}
          />{" "}
          <DataRow
            alt
            styles={styles}
            cells={[
              "Current Monthly Payment",
              cmMonthly > 0 ? toMoney(cmMonthly) : "-",
            ]}
            flexes={[1.5, 1]}
          />
          <DataRow
            styles={styles}
            cells={["Current Interest Rate", cmApr > 0 ? toPct(cmApr) : "-"]}
            flexes={[1.5, 1]}
          />
          <DataRow
            alt
            styles={styles}
            cells={[
              "Standard Variable Payment",
              cmSvrMonthly > 0 ? toMoney(cmSvrMonthly) : "-",
            ]}
            flexes={[1.5, 1]}
          />
          <DataRow
            styles={styles}
            cells={[
              "Standard Variable Rate",
              cmSvrApr > 0 ? toPct(cmSvrApr) : "-",
            ]}
            flexes={[1.5, 1]}
          />
          <DataRow
            alt
            styles={styles}
            cells={[
              "Remaining time on initial fixed period",
              cmInitPeriodMonths > 0
                ? formatYearsMonths(cmInitPeriodMonths)
                : "-",
            ]}
            flexes={[1.5, 1]}
          />
          <DataRow
            // alt
            styles={styles}
            cells={[
              "Total Loan Term",
              cmTermMonths > 0 ? formatYearsMonths(cmTermMonths) : "-",
            ]}
            flexes={[1.5, 1]}
          />
          {/* <DataRow
            styles={styles}
            cells={[
              "Term",
              cmTermMonths > 0 ? formatYearsMonths(cmTermMonths) : "-",
            ]}
            flexes={[1.5, 1]}
          /> */}
          {/* <DataRow
            alt
            styles={styles}
            cells={[
              "Monthly Payment",
              cmMonthly > 0 ? toMoney(cmMonthly) : "-",
            ]}
            flexes={[1.5, 1]}
          /> */}
          {/* Optional: show stepped info if provided */}
          {cmInitPeriodMonths > 0 ? (
            <DataRow
              styles={styles}
              cells={[
                "Initial Fixed Period",
                formatYearsMonths(cmInitialYears * 12 + cmInitialMonths),
              ]}
              flexes={[1.5, 1]}
            />
          ) : null}
        </View>

          </View>;
        })}
        <View style={styles.verticalBox} />
        {debts.length > 0 && (
          <>
            <Text style={styles.subheading}>Debt to consolidate</Text>
            <View style={styles.table}>
              {/* Match the UI columns: ID, Type, Balance, Term (years), Monthly, Interest */}
              <HeaderRow
                styles={styles}
                headers={[
                  "ID",
                  "Type of Debt",
                  "Balance (£)",
                  "Term (Years)",
                  "Monthly Payment (£)",
                  "Interest Rate (%)",
                ]}
                flexes={[0.5, 1.2, 1.2, 1, 1.6, 1.3]}
              />
              {(debts || []).length === 0 ? (
                <DataRow
                  styles={styles}
                  cells={["", "No debts entered.", "", "", "", ""]}
                  flexes={[0.5, 1.2, 1.2, 1, 1.6, 1.3]}
                />
              ) : (
                debts.map((d, idx) => {
                  const label = (d.label || "").trim();
                  const isRevolving = [
                    "Credit Card",
                    "Store Card",
                    "Overdraft",
                  ].includes(label);
                  const hasMinApplied =
                    label === "Credit Card" &&
                    isNum(Number(d.monthly)) &&
                    isNum(Number(d.plannedMonthly)) &&
                    Number(d.monthly) !== Number(d.plannedMonthly);

                  const termDisplay = isRevolving
                    ? isNum(Number(d.termYears))
                      ? String(safeFixed(Number(d.termYears), 2))
                      : "Auto"
                    : String(safeFixed(Number(d.termYears || 0), 2));

                  const monthlyBase = isRevolving
                    ? isNum(Number(d.monthly))
                      ? Number(d.monthly)
                      : Number(d.plannedMonthly || 0)
                    : Number(d.monthly || 0);

                  const monthlyDisplay = `${toMoney(monthlyBase)}${
                    hasMinApplied ? "" : ""
                  }`;

                  return (
                    <DataRow
                      key={idx}
                      styles={styles}
                      cells={[
                        fmtInt(d.id),
                        fmtText(d.label, ""),
                        toMoney(Number(d.balance || 0)),
                        termDisplay,
                        monthlyDisplay,
                        toPct(Number(d.apr || 0)),
                      ]}
                      flexes={[0.5, 1.6, 1.2, 1, 1.4, 1.1]}
                      alt={idx % 2 === 1}
                      alignRights={[false, false, false, false, false, false]}
                    />
                  );
                })
              )}
            </View>
          </>
        )}
        <Text style={styles.subheading} break>
          New Consolidation Mortgage
        </Text>
        <View style={styles.table}>
          <HeaderRow
            styles={styles}
            headers={["Entity", "Value"]}
            flexes={[1.5, 1]}
          />
          <DataRow
            styles={styles}
            cells={["Loan Amount", toMoney(loanAmount || 0)]}
            flexes={[1.5, 1]}
          />
          <DataRow
            styles={styles}
            cells={["Costs and Fees", feeDisplay]}
            flexes={[1.5, 1]}
            alt
          />
          <DataRow
            styles={styles}
            cells={["Initial Interest Rate", initialRate || "0%"]}
            flexes={[1.5, 1]}
          />
          <DataRow
            alt
            styles={styles}
            cells={["Standard Variable Rate", svrRate || "0%"]}
            flexes={[1.5, 1]}
          />
          <DataRow
            styles={styles}
            cells={["Initial Fixed Period", initialPeriodText]}
            flexes={[1.5, 1]}
          />
          <DataRow
            alt
            styles={styles}
            cells={["Loan Term", loanTermText || "0y 0m"]}
            flexes={[1.5, 1]}
          />
          {/* Monthly payments preview widgets with (overridden) tags */}
          <DataRow
            styles={styles}
            cells={[
              "Initial Monthly Payment",
              `${dashIfNaN(initialMonthlyDisplay)}${
                initialWasOverridden ? "" : ""
              }`,
            ]}
            flexes={[1.5, 1]}
          />
          <DataRow
            alt
            styles={styles}
            cells={[
              "Monthly Payment After the Initial Period",
              `${dashIfNaN(reversionMonthlyDisplay)}${
                reversionWasOverridden ? "" : ""
              }`,
            ]}
            flexes={[1.5, 1]}
          />
        </View>
        {warnNeverPaysOff ? (
          <Text style={[styles.content, { marginTop: 6 }]}>
            <Text style={styles.subcontent}>Warning: </Text>
            Your combined monthly payment is not enough to reduce the existing
            balance at the current blended APR.
          </Text>
        ) : null}
      </Page>

      {/* ========= Results & Comparisons ========= */}
      <Page style={styles.pageWithChrome} wrap>
        <Header primaryColor={primaryColor} />
        <Footer primaryColor={primaryColor} regulatoryText={regulatoryText} />
        <Text style={styles.subheading}>Summary</Text>
        {narrative ? <Text style={styles.content}>{narrative}</Text> : null}
        <View style={styles.table}>
          <HeaderRow
            styles={styles}
            headers={["", "Existing Debts", "Consolidation Mortgage"]}
            flexes={[1, 1, 1.25]}
          />
          <DataRow
            styles={styles}
            cells={[
              "Rates",
              toPct(weightedApr),
              <View style={{ flexDirection: "column", gap: 0 }}>
                <Text style={styles.content} break={false}>
                  Initial Fixed Rate: {toPct(initialApr)}
                </Text>
                <Text style={styles.content} break>
                  Standard Variable Rate: {toPct(svrApr)}
                </Text>
              </View>,
            ]}
            flexes={[1, 1, 1.25]}
          />
          <DataRow
            styles={styles}
            cells={[
              "Time to Payoff",
              isNum(nExisting) ? `${Math.ceil(nExisting)} months` : "-",
              isNum(months) ? `${months} months` : "-",
            ]}
            flexes={[1, 1, 1.25]}
            alt
          />
          <DataRow
            styles={styles}
            cells={["Associated Fees", "£0", toMoney(fee)]}
            flexes={[1, 1, 1.25]}
          />
          {/* <DataRow
            styles={styles}
            cells={["Total Level of Borrowing", "£0", toMoney(loanAmount)]}
            flexes={[1, 1, 1.25]}
            alt
          /> */}
          <DataRow
            styles={styles}
            cells={[
              "Monthly Pay",
              toMoney(safeFixed(totalMonthly)),
              <View style={{ flexDirection: "column", gap: 0 }}>
                <Text style={styles.content} break={false}>
                  {dashIfNaN(
                    isNum(monthlyPayInitial) && monthlyPayInitial > 0
                      ? monthlyPayInitial
                      : monthlyPayNew,
                  )}
                  {" "}(Initial Fixed Rate)
                </Text>
                <Text style={styles.content} break>
                  {isNum(monthlyPayReversion) && monthlyPayReversion > 0
                    ? `${toMoney(safeFixed(monthlyPayReversion))} (Standard Variable Rate)`
                    : ""}
                </Text>
              </View>,
              ,
            ]}
            flexes={[1, 1, 1.25]}
          />
          <DataRow
            styles={styles}
            cells={[
              "Total Amount to be Repaid",
              toMoney(safeFixed(totalPaymentsExisting || 0)),
              toMoney(safeFixed(totalPaymentsNew || 0)),
            ]}
            flexes={[1, 1, 1.25]}
            alt
          />
          <DataRow
            styles={styles}
            cells={[
              "Total Interest Over the Term",
              toMoney(safeFixed(totalInterestExisting || 0)),
              toMoney(safeFixed(totalInterestNew || 0)),
            ]}
            flexes={[1, 1, 1.25]}
          />
        </View>
        {/* <View style={styles.verticalBox} />
        <Text style={styles.content}>
          However, consolidating the existing unsecured debt into a new secured
          mortgage would result in an additional{" "}
          {toMoney(
            safeFixed(
              Math.abs(totals.totalInterestNew - totals.totalInterestExisting),
            ),
          )}{" "}
          in interest costs over the full term of the mortgage.
        </Text> */}
        {/* <View style={styles.verticalBox} /> */}
        <View style={styles.verticalBox} />
        <View style={{ ...styles.row, alignItems: "center", gap: 10 }}>
          <View style={{ flex: 3 }}>
            <Text style={styles.subheading}>Additional Guidance</Text>
            <Text style={styles.content}>
              When you add unsecured debts (such as credit cards, personal loans
              or store cards) to your mortgage, those debts become secured
              against your home. This means that if you don’t keep up with your
              mortgage payments, your home could be at risk.
            </Text>
            <View style={styles.verticalBox} />
            <Text style={styles.content}>
              Although consolidating debt in this way can make your monthly
              payments lower and easier to manage, it can also mean you pay more
              interest over time. It’s important to think carefully about your
              budget and make sure the new payments are affordable both now and
              in the future.
            </Text>
            <View style={styles.verticalBox} />
            <Text style={styles.content}>
              If you ever find yourself struggling to manage your money or would
              like help planning your budget, free and confidential advice is
              available from StepChange Debt Charity. You can call them on 0800
              138 1111 or visit www.stepchange.org for practical support and
              guidance.
            </Text>
          </View>
          <AdditionalInformationIcon size={64} primary={primaryColor} />
        </View>
      </Page>
    </Document>
  );
};

export default DCCReport;
